import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { authApi } from "@/api/auth";
import { Alert, Button, Card, Input } from "@/components/common";
import { ApiError } from "@/types/api";

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  function validate(): boolean {
    if (!email.trim()) {
      setError("Adres e-mail jest wymagany");
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Podaj prawidłowy adres e-mail");
      return false;
    }
    setError("");
    return true;
  }

  async function handleSubmit() {
    if (!validate()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await authApi.forgotPassword(email.trim());
      setIsSuccess(true);
    } catch (err) {
      if (err instanceof ApiError && err.statusCode === 404) {
        setIsSuccess(true);
      } else {
        setError(
          err instanceof ApiError
            ? err.message
            : "Wystąpił błąd. Spróbuj ponownie później.",
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen">
      {/* Left branding panel */}
      <div className="relative hidden overflow-hidden bg-linear-to-br from-adara-700 via-adara-800 to-adara-950 lg:flex lg:w-[45%] lg:flex-col lg:items-center lg:justify-center">
        <div className="absolute -top-32 -left-32 h-96 w-96 rounded-full bg-panel/5" />
        <div className="absolute -right-20 -bottom-20 h-72 w-72 rounded-full bg-panel/5" />
        <div className="absolute top-1/4 right-1/4 h-40 w-40 rounded-full bg-gold-400/10" />

        <div className="relative z-10 flex flex-col items-center px-12">
          <img
            src="/logos/adara-logo-white.svg"
            alt="Adara"
            className="mb-8 w-72"
          />
          <div className="mb-6 h-0.5 w-16 bg-linear-to-r from-gold-300 to-gold-500" />
          <p className="text-center text-sm tracking-widest text-white/70 uppercase">
            Panel Agenta
          </p>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex flex-1 flex-col items-center justify-center bg-subtle px-4 py-12 sm:px-6 lg:px-8">
        <div className="mb-8 lg:hidden">
          <img src="/logos/adara-logo.svg" alt="Adara" className="h-12" />
        </div>

        <Card padding="lg" className="w-full max-w-md animate-fade-in">
          {isSuccess ? (
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                <svg
                  className="h-6 w-6 text-green-600"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>

              <h1 className="mb-2 text-2xl font-semibold text-main">
                Sprawdź swoją skrzynkę
              </h1>
              <p className="mb-6 text-sm text-dim">
                Jeśli konto z adresem <strong>{email}</strong> istnieje,
                wysłaliśmy na nie link do resetowania hasła.
              </p>

              <Link
                to="/login"
                search={{ redirect: undefined }}
                className="text-sm font-medium text-adara-700 transition-colors hover:text-adara-500"
              >
                Wróć do logowania
              </Link>
            </div>
          ) : (
            <>
              <div className="mb-8">
                <h1 className="text-2xl font-semibold text-main">
                  Resetuj hasło
                </h1>
                <p className="mt-1.5 text-sm text-dim">
                  Podaj adres e-mail powiązany z Twoim kontem, a wyślemy Ci link
                  do resetowania hasła.
                </p>
              </div>

              {error && (
                <div className="mb-6">
                  <Alert
                    variant="error"
                    onDismiss={() => {
                      setError("");
                    }}
                  >
                    {error}
                  </Alert>
                </div>
              )}

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void handleSubmit();
                }}
                aria-label="Formularz resetowania hasła"
                noValidate
              >
                <div className="flex flex-col gap-5">
                  <Input
                    label="Adres e-mail"
                    type="email"
                    autoComplete="email"
                    placeholder="jan.kowalski@firma.pl"
                    value={email}
                    error={error && !email.trim() ? error : undefined}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (error) {
                        setError("");
                      }
                    }}
                    leftIcon={
                      <svg
                        className="h-5 w-5"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                      </svg>
                    }
                  />

                  <Button
                    type="submit"
                    fullWidth
                    size="lg"
                    isLoading={isSubmitting}
                  >
                    Wyślij link resetujący
                  </Button>
                </div>
              </form>

              <p className="mt-6 text-center text-sm text-dim">
                Pamiętasz hasło?{" "}
                <Link
                  to="/login"
                  search={{ redirect: undefined }}
                  className="font-medium text-adara-700 transition-colors hover:text-adara-500"
                >
                  Zaloguj się
                </Link>
              </p>
            </>
          )}
        </Card>

        <p className="mt-8 text-center text-xs text-faint">
          &copy; {new Date().getFullYear()} Adara. Wszelkie prawa zastrzeżone.
        </p>
      </div>
    </div>
  );
}
